import React, { useLayoutEffect, useState } from 'react';
import './modal.scss';

// Each dialog needs an id of its own for aria-labelledby to point at. React 16
// has no useId, and a fixed string would collide the moment two dialogs are
// mounted together — so instances are numbered as they appear.
let instanceCount = 0;

/**
 * The shell every modal in this app sits in: the scrim, the dialog box, the
 * stacking order, and the measurement that centres the box on what the operator
 * is actually reading.
 *
 * That last part is why this is a component and not just a stylesheet. The
 * scrim and the dialog have two different jobs, and conflating them is a bug in
 * both directions — see the note in modal.scss. The inset that separates them
 * can only be measured, never hardcoded: the width of whatever sits beside the
 * content (in the admin layout, the sidebar) belongs to another component, and
 * it collapses on narrow screens, where the correct inset is zero.
 *
 * Render it conditionally, the way the pending-action pattern already does
 * (`{isOpen && <Modal …>}`). Mounting IS the open event, so the measurement
 * below runs at the moment the dialog appears and cannot be stale — the content
 * can move without the window ever changing size.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.title - Names the dialog; also its accessible name.
 * @param {React.ReactNode} props.children - Everything below the title, including the buttons.
 * @param {React.RefObject<HTMLElement>} [props.contentRef] - The content column to
 *   centre on. A ref rather than a selector on purpose: a selector is a string
 *   that can go stale against a class rename with nothing to catch it, while a
 *   ref cannot point at an element that is not mounted. Omit it and the dialog
 *   centres on the window, which is right for a full-width page.
 * @param {string} [props.className] - A consumer class on the dialog box, for skinning
 *   its own content. The shell stays unthemed.
 * @param {string} [props.testId] - data-testid for the scrim, which wraps the whole dialog.
 * @returns {JSX.Element}
 */
export default function Modal({
  title, children, contentRef, className, testId,
}) {
  const [titleId] = useState(() => {
    instanceCount += 1;
    return `modal-title-${instanceCount}`;
  });

  // Null until measured, and null for as long as there is nothing to measure
  // against — which the stylesheet's fallback reads as "centre on the window".
  const [contentInset, setContentInset] = useState(null);

  // Both edges, not just the left: the content is inset on the right too, and
  // matching only one edge still leaves the dialog off-centre.
  //
  // useLayoutEffect, NOT useEffect: this measurement decides where the dialog
  // sits, so it has to land before the browser paints. With useEffect the first
  // paint used the window-centred fallback and the dialog visibly jumped into
  // place on the next frame — ~130px on a sidebar layout. Caught by local QA
  // reading the geometry on the same frame the dialog appeared.
  useLayoutEffect(() => {
    function measure() {
      const content = contentRef && contentRef.current;
      if (!content) return;

      const box = content.getBoundingClientRect();
      setContentInset({
        left: Math.max(0, Math.round(box.left)),
        right: Math.max(0, Math.round(window.innerWidth - box.right)),
      });
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [contentRef]);

  const scrimStyle = contentInset ? {
    '--modal-scrim-left': `${contentInset.left}px`,
    '--modal-scrim-right': `${contentInset.right}px`,
  } : undefined;

  return (
    <div className="modal__scrim" style={scrimStyle} data-testid={testId}>
      <div
        className={['modal', className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 className="modal__title" id={titleId}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
